import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Warning, CheckCircle, Sparkle, Info, X, Camera, FileText } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import LocationPicker from '@/components/LocationPicker';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const verdictStyles = {
  GREEN: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Approved', Icon: CheckCircle },
  AMBER: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', label: 'Requires Review', Icon: Warning },
  RED:   { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    label: 'Blocked', Icon: X },
};

// Strip the data:image/...;base64, prefix so we send raw base64 to the backend.
const stripPrefix = (b64) => (b64 || '').replace(/^data:image\/[^;]+;base64,/, '');

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const CreateParcel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [declaration, setDeclaration] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [photos, setPhotos] = useState([]); // [{name, dataUrl}]
  const [categoryBuckets, setCategoryBuckets] = useState({ allowed: [], restricted: [], prohibited: [] });
  const [formData, setFormData] = useState({
    origin_country: '',
    origin_city: '',
    destination_country: '',
    destination_city: '',
    weight: '',
    category: '',
    title: '',
    description: ''
  });

  // Fetch the legal declaration text once
  useEffect(() => {
    fetch(`${API}/compliance/declaration`)
      .then((r) => r.json())
      .then((d) => setDeclaration(d.text || ''))
      .catch(() => {});
  }, []);

  // Re-fetch the route-specific category matrix whenever the route changes.
  useEffect(() => {
    const o = formData.origin_country.trim();
    const d = formData.destination_country.trim();
    let cancelled = false;
    if (!o || !d) {
      // Defer state set so React effects rule doesn't flag synchronous setState
      Promise.resolve().then(() => { if (!cancelled) setCategoryBuckets({ allowed: [], restricted: [], prohibited: [] }); });
      return () => { cancelled = true; };
    }
    const url = `${API}/compliance/categories?origin=${encodeURIComponent(o)}&destination=${encodeURIComponent(d)}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => { if (!cancelled) setCategoryBuckets(data); })
      .catch(() => { if (!cancelled) setCategoryBuckets({ allowed: [], restricted: [], prohibited: [] }); });
    return () => { cancelled = true; };
  }, [formData.origin_country, formData.destination_country]);

  // Reset compliance result if the user mutates inputs after running a check.
  const setForm = (patch) => {
    setFormData((p) => ({ ...p, ...patch }));
    setCompliance(null);
  };

  const handleChange = (e) => setForm({ [e.target.name]: e.target.value });

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    const out = [];
    for (const f of files) {
      if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
        toast.error(`Skipping ${f.name}: only PNG/JPEG/WEBP allowed.`);
        continue;
      }
      const dataUrl = await fileToBase64(f);
      out.push({ name: f.name, dataUrl });
    }
    setPhotos((p) => [...p, ...out].slice(0, 4));
    setCompliance(null);
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const runComplianceCheck = async () => {
    if (!formData.destination_country || !formData.category || !formData.description) {
      toast.error('Fill destination, category and description first.');
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(`${API}/compliance/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title || formData.category,
          description: formData.description,
          category: formData.category,
          weight: parseFloat(formData.weight) || 0,
          origin_country: formData.origin_country,
          destination_country: formData.destination_country,
          quantity: 1,
          photo_base64_list: photos.map((p) => stripPrefix(p.dataUrl)),
        }),
      });
      const data = await res.json();
      setCompliance(data);
      if (data.verdict === 'RED') toast.error('This parcel is blocked by the compliance rules.');
      else if (data.verdict === 'AMBER') toast.warning('Review required — see flags below.');
      else toast.success('Compliance check passed.');
    } catch (e) {
      toast.error('Compliance check failed.');
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error('You must accept the compliance declaration first.');
      return;
    }
    if (compliance?.verdict === 'RED') {
      toast.error('Cannot submit — this parcel is blocked.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/parcels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          weight: parseFloat(formData.weight),
          declaration_accepted: true,
          photo_urls: [], // server-side image scan already done via base64
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === 'string'
          ? data.detail
          : (data.detail?.violations || []).join(' • ') || 'Failed to create parcel.';
        throw new Error(msg);
      }
      toast.success('Parcel created.');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.message || 'Failed to create parcel.');
      setLoading(false);
    }
  };

  const vStyle = compliance ? verdictStyles[compliance.verdict] : null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navigation />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1
          className="text-4xl font-extrabold text-gray-900 mb-8"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          data-testid="create-parcel-title"
        >
          Add New Parcel
        </h1>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="space-y-6">
            {/* Route — Beta: AU ↔ BD only via searchable dropdowns */}
            <div>
              <Label>Origin (Country · City)</Label>
              <div className="mt-2">
                <LocationPicker
                  value={{ country: formData.origin_country, city: formData.origin_city }}
                  onChange={({ country, city }) => setFormData((p) => ({ ...p, origin_country: country, origin_city: city }))}
                  testIdPrefix="origin"
                />
              </div>
            </div>
            <div>
              <Label>Destination (Country · City)</Label>
              <div className="mt-2">
                <LocationPicker
                  value={{ country: formData.destination_country, city: formData.destination_city }}
                  onChange={({ country, city }) => setFormData((p) => ({ ...p, destination_country: country, destination_city: city }))}
                  testIdPrefix="destination"
                />
              </div>
            </div>

            {/* Title + weight */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="title">Parcel Title</Label>
                <Input id="title" name="title" placeholder="e.g., Wedding documents"
                       value={formData.title} onChange={handleChange}
                       className="mt-2" data-testid="title" />
              </div>
              <div>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input id="weight" name="weight" type="number" step="0.1" required
                       value={formData.weight} onChange={handleChange}
                       className="mt-2" data-testid="weight" />
              </div>
            </div>

            {/* Dynamic category dropdown — prohibited items are hidden entirely */}
            <div>
              <Label htmlFor="category">Category</Label>
              <select id="category" name="category" required value={formData.category}
                      onChange={handleChange}
                      className="mt-2 w-full h-10 px-3 py-2 rounded-md border border-gray-200 bg-white text-sm"
                      data-testid="category-select">
                <option value="">Select a category…</option>
                {categoryBuckets.allowed.length === 0 && categoryBuckets.restricted.length === 0 ? (
                  <option disabled>Enter origin + destination to load categories</option>
                ) : (
                  <>
                    {categoryBuckets.allowed.length > 0 && (
                      <optgroup label="Allowed">
                        {categoryBuckets.allowed.map((c) => (
                          <option key={c.category} value={c.category} label={c.category}>
                            {`${c.category}`}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {categoryBuckets.restricted.length > 0 && (
                      <optgroup label="Restricted (extra docs may be required)">
                        {categoryBuckets.restricted.map((c) => (
                          <option key={c.category} value={c.category} label={`${c.category} ⚠`}>
                            {`${c.category} ⚠`}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </>
                )}
              </select>
              {/* Hint for required documents when a restricted category is chosen */}
              {(() => {
                const r = categoryBuckets.restricted.find((c) => c.category === formData.category);
                if (!r) return null;
                return (
                  <div className="mt-2 text-xs bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-testid="category-restricted-note">
                    <p className="font-semibold text-yellow-800 flex items-center gap-1.5">
                      <Warning size={14} weight="fill" /> Restricted category
                    </p>
                    <p className="text-yellow-700 mt-0.5">{r.notes}</p>
                    {r.required_documents?.length > 0 && (
                      <p className="text-yellow-700 mt-1">Required docs: {r.required_documents.join(', ')}</p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" name="description" required rows={4}
                        placeholder="Describe the parcel contents in detail…"
                        value={formData.description} onChange={handleChange}
                        className="mt-2" data-testid="description" />
            </div>

            {/* Photo uploads */}
            <div>
              <Label>Photos (optional — AI vision scan)</Label>
              <div className="mt-2 flex flex-wrap gap-3" data-testid="photo-grid">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                    <img src={p.dataUrl} alt={p.name} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center"
                            data-testid={`photo-remove-${i}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ))}
                {photos.length < 4 && (
                  <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#6D28D9] text-gray-400 hover:text-[#6D28D9]"
                         data-testid="photo-upload-button">
                    <Camera size={22} weight="duotone" />
                    <span className="text-[10px] font-semibold mt-1">Add photo</span>
                    <input type="file" accept="image/png,image/jpeg,image/webp" multiple
                           className="hidden" onChange={handlePhotoUpload} />
                  </label>
                )}
              </div>
            </div>

            {/* Compliance engine panel */}
            <div className="rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg dropio-gradient flex items-center justify-center flex-shrink-0">
                  <Sparkle size={20} weight="fill" className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">Border Compliance & AI Check</p>
                  <p className="text-xs text-gray-600">Rules engine validates the route. AI scans your description and photos for hidden issues.</p>
                </div>
                <button type="button" onClick={runComplianceCheck} disabled={checking}
                        className="dropio-gradient-cta text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                        data-testid="compliance-check-btn">
                  {checking ? 'Scanning…' : 'Run Check'}
                </button>
              </div>

              {compliance && vStyle && (
                <div className={`mt-3 p-4 rounded-lg border ${vStyle.bg} ${vStyle.border}`} data-testid="compliance-result">
                  <div className="flex items-center gap-2 mb-2">
                    <vStyle.Icon size={20} weight="fill" className={vStyle.text} />
                    <p className={`font-bold ${vStyle.text}`} data-testid="compliance-verdict">
                      {compliance.verdict} — {vStyle.label}
                    </p>
                    <span className="ml-auto text-xs font-bold text-gray-600 capitalize">Status: {compliance.status}</span>
                  </div>
                  {compliance.violations?.length > 0 && (
                    <div className="text-sm">
                      <p className="font-semibold text-red-700">Violations:</p>
                      <ul className="text-gray-700 space-y-0.5 mt-1">
                        {compliance.violations.map((v, i) => <li key={i}>• {v}</li>)}
                      </ul>
                    </div>
                  )}
                  {compliance.warnings?.length > 0 && (
                    <div className="text-sm mt-2">
                      <p className="font-semibold text-yellow-700">Warnings:</p>
                      <ul className="text-gray-700 space-y-0.5 mt-1">
                        {compliance.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                      </ul>
                    </div>
                  )}
                  {compliance.advisory?.length > 0 && (
                    <div className="text-sm mt-2">
                      <p className="font-semibold text-purple-700">AI advisory:</p>
                      <ul className="text-gray-700 space-y-0.5 mt-1">
                        {compliance.advisory.map((a, i) => <li key={i}>• {a}</li>)}
                      </ul>
                    </div>
                  )}
                  {compliance.required_documents?.length > 0 && (
                    <p className="text-xs text-gray-600 mt-3 border-t border-gray-200 pt-2">
                      <strong>Required documents:</strong> {compliance.required_documents.join(', ')}
                    </p>
                  )}
                </div>
              )}

              <Link to="/restricted-goods" className="text-xs font-semibold text-[#6D28D9] hover:underline flex items-center gap-1 mt-3" data-testid="view-restricted-link">
                <Info size={12} weight="bold" /> View restricted items guide
              </Link>
            </div>

            {/* Mandatory compliance declaration */}
            <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                     className="mt-1 w-4 h-4 accent-[#6D28D9]" data-testid="declaration-checkbox" />
              <div className="text-xs text-gray-700">
                <p className="font-semibold text-gray-900 mb-1 flex items-center gap-1.5">
                  <FileText size={14} weight="fill" className="text-[#6D28D9]" />
                  Compliance Declaration (required)
                </p>
                <p data-testid="declaration-text">{declaration || 'Loading declaration…'}</p>
              </div>
            </label>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={loading || !accepted || compliance?.verdict === 'RED'}
                      className="flex-1 dropio-gradient-cta text-white rounded-lg py-6 text-lg font-semibold disabled:opacity-60"
                      data-testid="submit-parcel">
                {loading ? 'Creating…' : 'Publish Parcel'}
              </Button>
              <Button type="button" onClick={() => navigate('/dashboard')}
                      className="bg-white text-gray-900 border border-gray-200 hover:border-[#6D28D9]/50 rounded-lg px-8 py-6"
                      data-testid="cancel-parcel">
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateParcel;
